import React, { useState } from 'react';

const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]));

export default function StockExportControls({ columns, rows, loading, location, search }) {
  const [selected, setSelected] = useState(() => columns.filter(column => !column.financial).map(column => column.key));
  const included = columns.filter(column => selected.includes(column.key));
  const values = () => rows.map(row => included.map(column => column.getValue(row) ?? '-'));
  const disabled = loading || !rows.length || !included.length;

  const exportCsv = () => {
    const quote = value => {
      const text = String(value ?? '');
      return `"${(/^[=+@\-\t\r]/.test(text) ? "'" : '') + text.replace(/"/g, '""')}"`;
    };
    const csv = [included.map(column => column.label), ...values()].map(row => row.map(quote).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `outlet-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const preview = window.open('', '_blank');
    if (!preview) { window.alert('Please allow pop-ups to open the PDF export.'); return; }
    preview.opener = null;
    const width = included.reduce((total, column) => total + column.width, 0);
    const orientation = 'portrait';
    const pageWidth = 198;
    const now = new Date();
    const date = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const time = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
    preview.onload = async () => {
      if (preview.document.fonts?.ready) await preview.document.fonts.ready;
      preview.focus();
      preview.print();
    };
    preview.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Manakirana Stock Sheet</title>
      <style>@page{size:A4 ${orientation};margin:6mm}*{box-sizing:border-box}body{font:11pt Arial,"Nirmala UI","Noto Sans Telugu",sans-serif;line-height:1.4;color:#111;margin:0;padding:0}table{width:100%;margin:0;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #ccc;padding:1.5mm;text-align:left;overflow-wrap:anywhere}th{background:#eee}thead{display:table-header-group}tr{break-inside:avoid}h1{font-size:16pt;text-align:center;margin:0 0 2mm}.header{background:white;border:0;padding:0 0 3mm}.meta{display:flex;flex-wrap:wrap;justify-content:space-between;gap:1mm 3mm;font-size:9pt;font-weight:normal;line-height:1.4}@media screen{body{max-width:${pageWidth}mm;margin:16px auto;padding:0}}@media print{button,.hint{display:none}}</style>
      </head><body><button onclick="window.print()">Print / Save as PDF</button><p class="hint">A4 ${orientation}, 6 mm margins. Use 100% scale and turn off browser Headers and footers. Select fewer export columns for wider product names.</p>
      <table><colgroup>${included.map(column => `<col style="width:${column.width / width * 100}%">`).join('')}</colgroup><thead>
      <tr><th colspan="${included.length}" class="header"><h1>Manakirana Stock Sheet</h1>
      <div class="meta"><span>Outlet Stock — ${escapeHtml(location)}</span><span>Date: ${escapeHtml(date)} &nbsp; Time: ${escapeHtml(time)}</span><span>Total Rows: ${rows.length}</span></div>
      ${search ? `<div class="meta">Search: ${escapeHtml(search)}</div>` : ''}</th></tr>
      <tr>${included.map(column => `<th>${escapeHtml(column.label)}</th>`).join('')}</tr></thead>
      <tbody>${values().map(row => `<tr>${row.map(value => `<td>${escapeHtml(value)}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`);
    preview.document.close();
  };

  return <div className="mb-4">
    <div className="mb-2 flex gap-2">
      <button type="button" disabled={disabled} onClick={exportCsv} className="rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50">Export CSV</button>
      <button type="button" disabled={disabled} onClick={exportPdf} className="rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50">Export PDF</button>
    </div>
    <details className="rounded-lg border p-3">
      <summary className="cursor-pointer text-sm font-semibold">Export columns ({included.length} selected)</summary>
      <p className="mt-2 text-xs text-gray-500">Choose columns for CSV and PDF. Financial columns are excluded by default.</p>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2">{columns.map(column => <label key={column.key} className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={selected.includes(column.key)} onChange={event => setSelected(current => event.target.checked ? [...current, column.key] : current.filter(key => key !== column.key))} />{column.label}
      </label>)}</div>
      {!included.length && <p className="mt-2 text-sm text-red-600">Select at least one column to export.</p>}
    </details>
  </div>;
}
